<template>
  <div class="date-wise-strength-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Date Wise Strength</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalStrength }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading date wise strength...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Date Wise Strength Report</span>
              <span class="date-range-text">
                As of <strong>{{ endDateDisplay }}</strong>
              </span>
              <span class="date-range-text">
                Generated on <strong>{{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="summary-section mb-4">
        <div class="summary-card summary-card--primary">
          <span class="label">Total Strength</span>
          <span class="value">{{ totalStrength }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Report Date</span>
          <span class="value value--sm">{{ endDateDisplay }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Regular Classes</span>
          <span class="value value--sm">{{ summaryRegularClassCount }}</span>
        </div>
        <div v-if="useCampusWiseLayout" class="summary-card">
          <span class="label">Campuses</span>
          <span class="value value--sm">{{ campusSections.length }}</span>
        </div>
        <div v-if="summaryExMatricClassCount" class="summary-card">
          <span class="label">Ex Matric</span>
          <span class="value value--sm">{{ summaryExMatricClassCount }}</span>
        </div>
      </div>

      <!-- Campus-wise layout (head office / all campuses) -->
      <template v-if="useCampusWiseLayout">
        <div class="report-section__title report-section__title--campus-root">
          Campus Wise Strength
        </div>
        <section
          v-for="(campus, campusIndex) in campusSections"
          :key="'campus-' + (campus.campusID || campusIndex)"
          class="campus-block"
        >
          <div class="campus-heading">
            <div class="campus-heading__left">
              <span class="campus-heading__index">{{ campusIndex + 1 }}</span>
              <div class="campus-heading__text">
                <span class="campus-heading__name">{{
                  campus.displayName
                }}</span>
              </div>
            </div>
            <span class="campus-heading__count"
              >{{ campus.totalStrength }} students</span
            >
          </div>

          <div
            v-if="campus.regularClasses.length"
            class="campus-inner-section"
          >
            <div class="report-section__title report-section__title--nested">
              Class Wise Strength
            </div>
            <div
              v-for="(cls, classIndex) in campus.regularClasses"
              :key="
                'c' +
                campus.campusID +
                '-reg-' +
                (cls.classID || classIndex)
              "
              class="class-block"
            >
              <div class="class-heading">
                <div class="class-heading__left">
                  <span class="class-heading__index">{{ classIndex + 1 }}</span>
                  <span class="class-heading__name">{{
                    cls.className || "Class"
                  }}</span>
                </div>
                <span class="class-heading__count"
                  >{{ classCount(cls) }} students</span
                >
              </div>
              <div class="table-container">
                <table class="ledger-style-table">
                  <thead>
                    <tr>
                      <th class="text-center col-sno">#</th>
                      <th class="text-center col-gr">GR. NO</th>
                      <th class="text-left">STUDENT NAME</th>
                      <th class="text-left">FATHER'S NAME</th>
                      <th class="text-center col-sec">SECTION</th>
                      <th class="text-center col-gender">GENDER</th>
                      <th class="text-center col-contact">MOBILE / PHONE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(student, index) in classStudents(cls)"
                      :key="student.id || index"
                    >
                      <td class="text-center font-weight-bold">
                        {{ index + 1 }}
                      </td>
                      <td class="text-center gr-cell">
                        {{ student.grNo || "-" }}
                      </td>
                      <td class="text-left font-weight-bold name-cell">
                        {{ displayName(student.name) }}
                      </td>
                      <td class="text-left">
                        {{
                          displayName(
                            student.fatherName || student.father_name
                          )
                        }}
                      </td>
                      <td class="text-center">
                        {{ student.sectionName || student.section || "-" }}
                      </td>
                      <td class="text-center">
                        {{ formatGender(student) }}
                      </td>
                      <td class="text-center contact-cell">
                        {{ formatContact(student) }}
                      </td>
                    </tr>
                    <tr v-if="!classStudents(cls).length">
                      <td colspan="7" class="text-center py-4 no-data">
                        No student records for this class
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div
            v-if="campus.exMatricClasses.length"
            class="campus-inner-section campus-inner-section--exmatric"
          >
            <div
              class="report-section__title report-section__title--nested report-section__title--exmatric"
            >
              Ex Matriculation Classes
            </div>
            <div
              v-for="(cls, classIndex) in campus.exMatricClasses"
              :key="
                'c' +
                campus.campusID +
                '-ex-' +
                (cls.classID || classIndex)
              "
              class="class-block class-block--exmatric"
            >
              <div class="class-heading class-heading--exmatric">
                <div class="class-heading__left">
                  <span class="class-heading__index">{{ classIndex + 1 }}</span>
                  <span class="class-heading__name">{{
                    cls.className || "Class"
                  }}</span>
                </div>
                <span class="class-heading__count"
                  >{{ classCount(cls) }} students</span
                >
              </div>
              <div class="table-container">
                <table class="ledger-style-table">
                  <thead>
                    <tr>
                      <th class="text-center col-sno">#</th>
                      <th class="text-center col-gr">GR. NO</th>
                      <th class="text-left">STUDENT NAME</th>
                      <th class="text-left">FATHER'S NAME</th>
                      <th class="text-center col-sec">SECTION</th>
                      <th class="text-center col-gender">GENDER</th>
                      <th class="text-center col-contact">MOBILE / PHONE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(student, index) in classStudents(cls)"
                      :key="student.id || index"
                    >
                      <td class="text-center font-weight-bold">
                        {{ index + 1 }}
                      </td>
                      <td class="text-center gr-cell">
                        {{ student.grNo || "-" }}
                      </td>
                      <td class="text-left font-weight-bold name-cell">
                        {{ displayName(student.name) }}
                      </td>
                      <td class="text-left">
                        {{
                          displayName(
                            student.fatherName || student.father_name
                          )
                        }}
                      </td>
                      <td class="text-center">
                        {{ student.sectionName || student.section || "-" }}
                      </td>
                      <td class="text-center">
                        {{ formatGender(student) }}
                      </td>
                      <td class="text-center contact-cell">
                        {{ formatContact(student) }}
                      </td>
                    </tr>
                    <tr v-if="!classStudents(cls).length">
                      <td colspan="7" class="text-center py-4 no-data">
                        No student records for this class
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <div
          v-if="!campusSections.length"
          class="text-center py-5 no-data"
        >
          <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
          <p>No campus records found for the selected criteria</p>
        </div>
      </template>

      <!-- Single-campus / flat classes -->
      <template v-else>
      <section v-if="regularClasses.length" class="report-section">
        <div class="report-section__title">Class Wise Strength</div>

        <div
          v-for="(cls, classIndex) in regularClasses"
          :key="'reg-' + (cls.classID || classIndex)"
          class="class-block"
        >
          <div class="class-heading">
            <div class="class-heading__left">
              <span class="class-heading__index">{{ classIndex + 1 }}</span>
              <span class="class-heading__name">{{
                cls.className || "Class"
              }}</span>
            </div>
            <span class="class-heading__count"
              >{{ classCount(cls) }} students</span
            >
          </div>

          <div class="table-container">
            <table class="ledger-style-table">
              <thead>
                <tr>
                  <th class="text-center col-sno">#</th>
                  <th class="text-center col-gr">GR. NO</th>
                  <th class="text-left">STUDENT NAME</th>
                  <th class="text-left">FATHER'S NAME</th>
                  <th class="text-center col-sec">SECTION</th>
                  <th class="text-center col-gender">GENDER</th>
                  <th class="text-center col-contact">MOBILE / PHONE</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(student, index) in classStudents(cls)"
                  :key="student.id || index"
                >
                  <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                  <td class="text-center gr-cell">{{ student.grNo || "-" }}</td>
                  <td class="text-left font-weight-bold name-cell">
                    {{ displayName(student.name) }}
                  </td>
                  <td class="text-left">
                    {{ displayName(student.fatherName || student.father_name) }}
                  </td>
                  <td class="text-center">
                    {{ student.sectionName || student.section || "-" }}
                  </td>
                  <td class="text-center">
                    {{ formatGender(student) }}
                  </td>
                  <td class="text-center contact-cell">
                    {{ formatContact(student) }}
                  </td>
                </tr>
                <tr v-if="!classStudents(cls).length">
                  <td colspan="7" class="text-center py-4 no-data">
                    No student records for this class
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section
        v-if="exMatricClasses.length"
        class="report-section report-section--exmatric"
      >
        <div class="report-section__title report-section__title--exmatric">
          Ex Matriculation Classes
        </div>

        <div
          v-for="(cls, classIndex) in exMatricClasses"
          :key="'ex-' + (cls.classID || classIndex)"
          class="class-block class-block--exmatric"
        >
          <div class="class-heading class-heading--exmatric">
            <div class="class-heading__left">
              <span class="class-heading__index">{{ classIndex + 1 }}</span>
              <span class="class-heading__name">{{
                cls.className || "Class"
              }}</span>
            </div>
            <span class="class-heading__count"
              >{{ classCount(cls) }} students</span
            >
          </div>

          <div class="table-container">
            <table class="ledger-style-table">
              <thead>
                <tr>
                  <th class="text-center col-sno">#</th>
                  <th class="text-center col-gr">GR. NO</th>
                  <th class="text-left">STUDENT NAME</th>
                  <th class="text-left">FATHER'S NAME</th>
                  <th class="text-center col-sec">SECTION</th>
                  <th class="text-center col-gender">GENDER</th>
                  <th class="text-center col-contact">MOBILE / PHONE</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(student, index) in classStudents(cls)"
                  :key="student.id || index"
                >
                  <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                  <td class="text-center gr-cell">{{ student.grNo || "-" }}</td>
                  <td class="text-left font-weight-bold name-cell">
                    {{ displayName(student.name) }}
                  </td>
                  <td class="text-left">
                    {{ displayName(student.fatherName || student.father_name) }}
                  </td>
                  <td class="text-center">
                    {{ student.sectionName || student.section || "-" }}
                  </td>
                  <td class="text-center">
                    {{ formatGender(student) }}
                  </td>
                  <td class="text-center contact-cell">
                    {{ formatContact(student) }}
                  </td>
                </tr>
                <tr v-if="!classStudents(cls).length">
                  <td colspan="7" class="text-center py-4 no-data">
                    No student records for this class
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div v-if="!useCampusWiseLayout && !sortedClasses.length" class="text-center py-5 no-data">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No student records found for the selected criteria</p>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "DateWiseStrengthPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportData: null,
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      endDate: "",
      classID: 0,
      sectionID: 0,
      departmentID: 0,
    };
  },
  computed: {
    useCampusWiseLayout() {
      if (Number(this.cID) !== 1) return false;
      const raw = this.reportData;
      if (!raw) return false;
      if (raw.campusWise === true || raw.isCampusWise === true) return true;
      const campuses = raw.campuses || raw.campusWiseData;
      return Array.isArray(campuses) && campuses.length > 0;
    },
    campusSections() {
      const raw = this.reportData;
      if (!raw || !this.useCampusWiseLayout) return [];

      const list = raw.campuses || raw.campusWiseData || [];
      return list.map((campus, index) => {
        const rawClasses =
          campus.classes || campus.Classes || campus.classList || [];
        const classes = [...rawClasses].sort((a, b) =>
          this.compareClasses(a, b)
        );
        const name =
          campus.campusName ||
          campus.name ||
          campus.organizationName ||
          "Campus";
        const branch =
          campus.campusBranch || campus.branch || campus.branchName || "";
        const displayName = branch && branch !== name ? `${name} — ${branch}` : name;

        return {
          campusID: campus.campusID ?? campus.id ?? campus.cID ?? index,
          campusName: name,
          campusBranch: branch,
          displayName,
          summary: campus.summary,
          regularClasses: classes.filter(
            (cls) => !this.isExMatricClass(cls.className)
          ),
          exMatricClasses: classes.filter((cls) =>
            this.isExMatricClass(cls.className)
          ),
          totalStrength: this.campusBlockStrength(campus, classes),
          regularClassCount: classes.filter(
            (cls) => !this.isExMatricClass(cls.className)
          ).length,
        };
      });
    },
    classes() {
      const raw = this.reportData;
      if (!raw) return [];
      if (Array.isArray(raw.classes) && raw.classes.length) {
        return raw.classes;
      }
      const campuses = raw.campuses || raw.campusWiseData;
      if (Array.isArray(campuses) && campuses.length) {
        const fromCampuses = campuses.flatMap(
          (c) => c.classes || c.Classes || c.classList || []
        );
        if (fromCampuses.length) return fromCampuses;
      }
      return [];
    },
    sortedClasses() {
      return [...this.classes].sort((a, b) => this.compareClasses(a, b));
    },
    regularClasses() {
      return this.sortedClasses.filter(
        (cls) => !this.isExMatricClass(cls.className)
      );
    },
    exMatricClasses() {
      return this.sortedClasses.filter((cls) =>
        this.isExMatricClass(cls.className)
      );
    },
    summaryRegularClassCount() {
      if (this.useCampusWiseLayout) {
        return this.campusSections.reduce(
          (sum, c) => sum + (c.regularClasses.length || 0),
          0
        );
      }
      return this.regularClasses.length;
    },
    summaryExMatricClassCount() {
      if (this.useCampusWiseLayout) {
        return this.campusSections.reduce(
          (sum, c) => sum + (c.exMatricClasses.length || 0),
          0
        );
      }
      return this.exMatricClasses.length;
    },
    totalStrength() {
      const summary = this.reportData && this.reportData.summary;
      if (summary) {
        const n = Number(summary.totalCount ?? summary.presentCount ?? 0);
        if (n) return n;
      }
      if (this.useCampusWiseLayout) {
        return this.campusSections.reduce(
          (sum, c) => sum + (Number(c.totalStrength) || 0),
          0
        );
      }
      return this.classes.reduce((sum, cls) => sum + this.classCount(cls), 0);
    },
    endDateDisplay() {
      return this.formatDate(this.endDate);
    },
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.endDate = query.endDate || "";
      this.classID = Number(query.classID || 0);
      this.sectionID = Number(query.sectionID || 0);
      this.departmentID = Number(query.departmentID || 0);

      if (!this.db || !this.endDate) {
        this.error =
          "Missing report parameters. Please close and try again from Reports.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    isExMatricClass(className) {
      const cls = String(className || "")
        .trim()
        .toUpperCase()
        .replace(/-/g, " ");
      return cls.includes("EX MATRIC") || cls.includes("EXMATRIC");
    },
    classSortOrder(className) {
      if (this.isExMatricClass(className)) return 900;

      const cls = String(className || "")
        .trim()
        .toUpperCase()
        .replace(/-/g, " ");

      if (cls.includes("PRE ADMISSION")) return 0;
      if (cls === "PG" || cls.startsWith("PG ")) return 1;
      if (cls.includes("PRE NURSERY")) return 2;
      if (cls.includes("NURSERY")) return 3;
      if (cls.includes("KINDER") || cls.includes("KG")) return 4;

      const tokens = cls.replace(/[^A-Z0-9]/g, " ").split(/\s+/);
      if (tokens.includes("XII") || tokens.includes("12")) return 16;
      if (tokens.includes("XI") || tokens.includes("11")) return 15;
      if (tokens.includes("X") || tokens.includes("10")) return 14;
      if (tokens.includes("IX") || tokens.includes("9")) return 13;
      if (tokens.includes("VIII") || tokens.includes("8")) return 12;
      if (tokens.includes("VII") || tokens.includes("7")) return 11;
      if (tokens.includes("VI") || tokens.includes("6")) return 10;
      if (tokens.includes("V") || tokens.includes("5")) return 9;
      if (tokens.includes("IV") || tokens.includes("4")) return 8;
      if (tokens.includes("III") || tokens.includes("3")) return 7;
      if (tokens.includes("II") || tokens.includes("2")) return 6;
      if (tokens.includes("I") || tokens.includes("1")) return 5;

      return 100;
    },
    compareClasses(a, b) {
      const exA = this.isExMatricClass(a.className) ? 1 : 0;
      const exB = this.isExMatricClass(b.className) ? 1 : 0;
      if (exA !== exB) return exA - exB;

      const seqA = this.getClassSequence(a);
      const seqB = this.getClassSequence(b);
      if (seqA !== seqB) return seqA - seqB;

      return String(a.className || "").localeCompare(
        String(b.className || ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      );
    },
    getClassSequence(cls) {
      if (cls.classSequence != null && cls.classSequence !== "") {
        return Number(cls.classSequence);
      }
      const firstStudent = (cls.students || [])[0];
      if (firstStudent && firstStudent.classSequence != null) {
        return Number(firstStudent.classSequence);
      }
      return this.classSortOrder(cls.className);
    },
    normalizeDateWiseStrengthPayload(data) {
      if (!data) return null;
      if (Array.isArray(data)) {
        return { campuses: data };
      }
      if (typeof data !== "object") return data;

      const campuses = data.campuses || data.campusWiseData;
      if (Array.isArray(campuses) && campuses.length) {
        return data;
      }

      const classes = Array.isArray(data.classes) ? data.classes : [];
      if (!classes.length) return data;

      const campusMap = {};
      const campusOrder = [];

      const campusKeyForClass = (cls) => {
        const cid =
          cls.campusID ??
          cls.cID ??
          cls.campusId ??
          (this.classStudents(cls)[0] &&
            (this.classStudents(cls)[0].campusID ??
              this.classStudents(cls)[0].cID));
        const cname =
          cls.campusBranch ||
          cls.campusName ||
          cls.branch ||
          cls.branchName ||
          "";
        if (cid != null && cid !== "") return `id-${cid}`;
        if (cname) return `name-${cname}`;
        return null;
      };

      classes.forEach((cls) => {
        const key = campusKeyForClass(cls);
        if (!key) return;

        if (!campusMap[key]) {
          const cid =
            cls.campusID ?? cls.cID ?? cls.campusId ?? key.replace(/^id-/, "");
          const name =
            cls.campusName || cls.organizationName || cls.branch || "Campus";
          const branch = cls.campusBranch || cls.branch || cls.branchName || "";
          campusMap[key] = {
            campusID: cid,
            campusName: name,
            campusBranch: branch,
            classes: [],
          };
          campusOrder.push(key);
        }
        campusMap[key].classes.push(cls);
      });

      if (campusOrder.length > 1 && Number(this.cID) === 1) {
        const { classes: _ignoredClasses, ...rest } = data;
        return {
          ...rest,
          campuses: campusOrder.map((key) => campusMap[key]),
        };
      }

      return data;
    },
    campusBlockStrength(campus, classes) {
      const summary = campus && campus.summary;
      if (summary) {
        const n = Number(summary.totalCount ?? summary.presentCount ?? 0);
        if (n) return n;
      }
      const list = classes || campus.classes || [];
      return list.reduce((sum, cls) => sum + this.classCount(cls), 0);
    },
    classCount(cls) {
      const count = cls.presentCount ?? cls.totalCount;
      if (count != null && count !== "") return Number(count) || 0;
      return this.classStudents(cls).length;
    },
    classStudents(cls) {
      let list = [];

      if (Array.isArray(cls.students) && cls.students.length) {
        list = cls.students;
      } else {
        const buckets = [
          ...(cls.presentStudents || []),
          ...(cls.registeredStudents || []),
          ...(cls.leftStudents || []),
        ];
        const seen = new Set();
        buckets.forEach((student) => {
          const key = student.id || `${student.grNo}-${student.name}`;
          if (seen.has(key)) return;
          seen.add(key);
          list.push(student);
        });
      }

      return [...list].sort((a, b) =>
        String(a.grNo || "").localeCompare(String(b.grNo || ""), undefined, {
          numeric: true,
        })
      );
    },
    formatGender(student) {
      return student.genderLabel || student.gender || "-";
    },
    displayName(value) {
      const text = String(value || "-").trim();
      return text === "-" ? text : text.toUpperCase();
    },
    formatContact(student) {
      const direct = [
        student.contact_father,
        student.contact_mother,
        student.contact_student,
        student.contact_home,
      ].find((value) => value && String(value).trim());

      if (direct) return String(direct).trim();

      const details = String(student.contactDetails || "").trim();
      if (details) {
        return (
          details
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)[0] || "-"
        );
      }

      return (
        student.contact ||
        student.mobile ||
        student.phone ||
        student.contacts ||
        "-"
      );
    },
    formatDate(val) {
      if (!val) return "-";
      const raw = String(val).slice(0, 10);
      const parts = raw.split("-");
      if (parts.length !== 3) return raw;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const currentBranch = this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/Students/ClassWiseStudentStatusReport",
          {
            params: {
              db: this.db,
              cID: this.cID,
              endDate: this.endDate,
              classID: this.classID,
              sectionID: this.sectionID,
              departmentID: this.departmentID,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (response.data && response.data.status === "success") {
          this.reportData = this.normalizeDateWiseStrengthPayload(
            response.data.data
          );
        } else {
          this.error =
            (response.data && response.data.message) ||
            "Failed to load date wise strength report.";
        }
      } catch (err) {
        console.error("Date wise strength report error:", err);
        this.error =
          (err.response && err.response.data && err.response.data.message) ||
          "Failed to load date wise strength report.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.date-wise-strength-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1rem;
    color: #444;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 120px;

    &--primary .value {
      color: #6c5ce7;
    }

    .label {
      font-size: 0.75rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.35rem;
      font-weight: 800;
      color: #2d3436;

      &--sm {
        font-size: 1.05rem;
      }
    }
  }
}

.report-section {
  margin-bottom: 2.5rem;

  &--exmatric {
    margin-top: 2.75rem;
    padding-top: 1.5rem;
    border-top: 3px double #cbd5e1;
  }
}

.report-section__title {
  font-size: 1rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #2c3e50;
  margin-bottom: 1.25rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #2c3e50;

  &--exmatric {
    color: #7f5539;
    border-bottom-color: #7f5539;
  }

  &--nested {
    font-size: 0.88rem;
    margin-bottom: 1rem;
    margin-top: 0.25rem;
    border-bottom-width: 1px;
    color: #475569;
    border-bottom-color: #cbd5e1;
  }

  &--campus-root {
    font-size: 1.05rem;
    color: #2c3e50;
    border-bottom-color: #2c3e50;
    margin-bottom: 1.5rem;
  }
}

.campus-block {
  margin-bottom: 2.5rem;
  page-break-inside: auto;
}

.campus-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.95rem 1.1rem;
  margin-bottom: 1.25rem;
  background: #f8f9fa;
  border: 2px solid #2c3e50;
  border-radius: 8px;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    min-width: 0;
  }

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    border-radius: 6px;
    background: #e9ecef;
    color: #2c3e50;
    border: 1px solid #adb5bd;
    font-size: 0.85rem;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }

  &__name {
    font-size: 1.05rem;
    font-weight: 800;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.35px;
    line-height: 1.25;
  }

  &__meta {
    font-size: 0.72rem;
    font-weight: 600;
    color: #6c757d;
    text-transform: none;
  }

  &__count {
    font-size: 0.88rem;
    font-weight: 800;
    color: #fff;
    background: #2c3e50;
    padding: 0.4rem 0.9rem;
    border-radius: 999px;
    white-space: nowrap;
  }
}

.campus-inner-section {
  padding-left: 0.35rem;
  margin-bottom: 1.5rem;

  &--exmatric {
    margin-top: 1.25rem;
    padding-top: 0.75rem;
    border-top: 2px dashed #e2e8f0;
  }
}

.class-block {
  margin-bottom: 1.75rem;

  &--exmatric {
    .class-heading__index {
      background: #fff4e6;
      color: #9a3412;
    }
  }
}

.class-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  margin-bottom: 0;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;

  &--exmatric {
    background: #fffaf3;
    border-color: #f0dcc8;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 0.8rem;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__name {
    font-size: 1rem;
    font-weight: 800;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  &__count {
    font-size: 0.82rem;
    font-weight: 700;
    color: #fff;
    background: #2c3e50;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    white-space: nowrap;
  }
}

.table-container {
  border: 1px solid #dee2e6;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0;
  border: none;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 11px 14px;
    box-sizing: border-box;
    font-size: 0.92rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.82rem;
      letter-spacing: 0.45px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }

  .gr-cell {
    font-weight: 700;
    color: #6c5ce7;
  }

  .name-cell {
    color: #111827;
  }

  .contact-cell {
    font-size: 0.88rem;
    color: #374151;
  }
}

.col-sno {
  width: 5%;
}

.col-gr {
  width: 10%;
}

.col-sec {
  width: 12%;
}

.col-gender {
  width: 10%;
}

.col-contact {
  width: 18%;
}

.no-data {
  color: #adb5bd;
}

@page {
  size: A4 portrait;
  margin: 10mm;
}

@media print {
  .no-print {
    display: none !important;
  }

  html,
  body {
    background: white !important;
    height: auto !important;
    overflow: visible !important;
  }

  .date-wise-strength-report {
    background: white !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    overflow: visible !important;
  }

  .report-header-modern {
    margin-bottom: 12px !important;
    padding-bottom: 10px !important;
    border-bottom: 2px solid #000 !important;
    page-break-inside: avoid;
    break-inside: avoid;

    .school-logo-img {
      height: 70px !important;
      max-width: 160px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.25rem !important;
    }

    .report-info-line {
      font-size: 0.8rem !important;
      gap: 0.4rem !important;
    }
  }

  .summary-section {
    margin: 8px 0 12px !important;
    padding: 6px 8px !important;
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-around !important;
    page-break-inside: avoid;
    break-inside: avoid;

    .summary-card {
      .label {
        font-size: 0.65rem !important;
      }

      .value {
        font-size: 0.95rem !important;
      }
    }
  }

  .report-section {
    margin-bottom: 10px !important;
    page-break-inside: auto;
    break-inside: auto;
  }

  .report-section__title {
    margin-bottom: 8px !important;
    padding-bottom: 4px !important;
    font-size: 0.85rem !important;
    page-break-after: avoid;
    break-after: avoid;
  }

  .report-section--exmatric {
    margin-top: 14px !important;
    padding-top: 10px !important;
    border-top: 2px solid #000 !important;
    page-break-before: auto;
    break-before: auto;
  }

  .class-block {
    margin-bottom: 12px !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  .campus-block {
    margin-bottom: 16px !important;
    page-break-inside: auto;
    break-inside: auto;
  }

  .campus-heading {
    page-break-after: avoid;
    break-after: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
    border: 2px solid #000 !important;
    border-radius: 0 !important;
    background: #f1f2f6 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    padding: 8px 10px !important;
    margin-bottom: 10px !important;

    &__index {
      background: #fff !important;
      color: #000 !important;
      border: 1px solid #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    &__count {
      background: #2c3e50 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .class-heading {
    page-break-after: avoid;
    break-after: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
    border: 1px solid #000 !important;
    border-bottom: none !important;
    border-radius: 0 !important;
    background: #f1f2f6 !important;
    padding: 6px 8px !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &__name {
      font-size: 0.85rem !important;
    }

    &__count {
      background: #333 !important;
      color: #fff !important;
      font-size: 0.7rem !important;
    }

    &__index {
      width: 22px !important;
      height: 22px !important;
      min-width: 22px !important;
      font-size: 0.7rem !important;
    }
  }

  .table-container {
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    overflow: visible !important;
    page-break-inside: auto;
    break-inside: auto;
  }

  .ledger-style-table {
    font-size: 8.5pt !important;
    width: 100% !important;
    page-break-inside: auto;
    break-inside: auto;

    thead {
      display: table-header-group;
    }

    tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    th,
    td {
      padding: 4px 6px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
}
</style>
